import {useEffect, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ArrowLeft, Ban, CheckCircle2, RefreshCw, XCircle} from "lucide-react";
import {Link, useParams} from "react-router-dom";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {getAuthToken} from "../../shared/api/client";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {DetailGrid} from "../../shared/components/DetailGrid";
import {FinancialCommandDialog} from "../../shared/components/FinancialCommandDialog";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {getActorCountryScopeBlock} from "../../shared/finance/actorCountryScope";
import {formatDateTime} from "../../shared/utils/format";
import {useTranslation} from "../../shared/i18n";
import {
    approveRefundRequest,
    cancelRefundRequest,
    findPostedRefund,
    getRefundRequest,
    refundKeys,
    rejectRefundRequest,
} from "./api";
import {
    actionsForRefund,
    getRefundActionPresentation,
    getRefundStatusPresentation,
    hasPostedRefund,
} from "./statusMachine";
import "./refunds.css";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ACTION_REQUESTS = {
    approve: approveRefundRequest,
    reject: rejectRefundRequest,
    cancel: cancelRefundRequest,
};

const ACTION_ICONS = {
    approve: CheckCircle2,
    reject: XCircle,
    cancel: Ban,
};

const decisionResult = (request, t) => {
    const statusLabel = t(`money.refunds.statuses.${getRefundStatusPresentation(request.status).known ? request.status : "unknown"}`);
    if (hasPostedRefund(request)) {
        return {kind: "success", message: t("money.refunds.detail.postedByServer")};
    }
    if (request.status === "failed") {
        return {
            kind: "error",
            message: t("money.refunds.detail.processedFailed", {
                code: request.failure_code || t("money.refunds.unknownFailure"),
            }),
        };
    }
    if (request.status === "rejected") {
        return {kind: "success", message: t("money.refunds.detail.rejected")};
    }
    if (request.status === "cancelled") {
        return {kind: "success", message: t("money.refunds.detail.cancelled")};
    }
    if (request.status === "approved") {
        return {
            kind: "success",
            message: t("money.refunds.detail.approvedPending"),
        };
    }
    return {
        kind: "error",
        message: t("money.refunds.unexpectedStatus", {status: statusLabel}),
    };
};

const actionFields = (action, t) =>
    action === "approve"
        ? []
        : [{name: "reason", label: t("money.refunds.detail.reasonField"), multiline: true, required: true}];

const PostedRefundEvidence = ({request, query}) => {
    const {t} = useTranslation();
    if (!hasPostedRefund(request)) return null;
    const postedRefund = query.data;

    return (
        <section className="refund-section" aria-labelledby="posted-refund-title">
            <div className="refund-section-heading">
                <div>
                    <p className="admin-eyebrow">{t("money.refunds.detail.postingsEyebrow")}</p>
                    <h2 id="posted-refund-title">{t("money.refunds.detail.postedTitle")}</h2>
                </div>
                <Link
                    className="refund-back-link"
                    to={`/finance/refund-history?payment_bill_id=${encodeURIComponent(request.pay_bill_id)}`}
                >
                    {t("money.refunds.detail.openHistory")}
                </Link>
            </div>
            {query.isLoading ? (
                <div className="admin-table-skeleton" aria-label={t("money.refunds.detail.postedLoading")}>
                    <span /><span /><span />
                </div>
            ) : query.isError ? (
                <DataState
                    title={t("money.refunds.detail.postedLoadError")}
                    copy={mapFinanceError(query.error).message}
                />
            ) : postedRefund ? (
                <>
                    <div className="refund-money-band" aria-label={t("money.refunds.detail.breakdownAria")}>
                        <span><small>{t("money.refunds.detail.total")}</small><strong>{formatExactMoney(postedRefund.amount)}</strong></span>
                        <span><small>{t("money.refunds.detail.main")}</small><strong>{formatExactMoney(postedRefund.main_amount)}</strong></span>
                        <span><small>{t("money.refunds.detail.points")}</small><strong>{formatExactMoney(postedRefund.points_amount)}</strong></span>
                        <span><small>Cashup</small><strong>{formatExactMoney(postedRefund.cashup_amount)}</strong></span>
                    </div>
                    <DetailGrid items={[
                        {label: t("money.refunds.detail.postingId"), value: postedRefund.id},
                        {label: t("money.refunds.detail.operator"), value: postedRefund.created_by_name || (postedRefund.created_by_id ? `#${postedRefund.created_by_id}` : "—")},
                        {label: t("money.refunds.detail.reason"), value: postedRefund.reason || "—"},
                        {label: t("money.refunds.detail.created"), value: formatDateTime(postedRefund.created_at)},
                    ]} />
                </>
            ) : (
                <DataState
                    title={t("money.refunds.detail.missingTitle")}
                    copy={t("money.refunds.detail.missingCopy", {id: request.posted_refund_id})}
                />
            )}
        </section>
    );
};

export const RefundDetailPage = () => {
    const {t} = useTranslation();
    const {requestId} = useParams();
    const validRequestId = UUID_PATTERN.test(requestId || "") ? requestId : null;
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [countryScope, setCountryScope] = useState(() =>
        resolveAdminCountryScope({
            role: capabilities.user?.role,
            allowedCountryIds: capabilities.scope?.country_ids || [],
            storedScope:
                localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
        }),
    );
    const activeCountryScopeRef = useRef(countryScope);
    const [command, setCommand] = useState(null);
    const [commandError, setCommandError] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const scopeBlock = getActorCountryScopeBlock({
        countryScope,
        actorCountryId: capabilities.country?.id,
    });
    const detailKey = refundKeys.requestDetail(countryScope, validRequestId);

    const detailQuery = useQuery({
        queryKey: detailKey,
        queryFn: () => getRefundRequest({
            countryScope,
            actorCountryId: capabilities.country?.id,
            requestId: validRequestId,
        }),
        enabled: validRequestId !== null && countryScope !== null && scopeBlock === null,
        retry: false,
    });

    const request = detailQuery.data;
    const postedHistoryQuery = useQuery({
        queryKey: refundKeys.postedEvidence(
            countryScope,
            request?.pay_bill_id,
            request?.posted_refund_id,
        ),
        queryFn: () => findPostedRefund({
            countryScope,
            paymentBillId: request.pay_bill_id,
            postedRefundId: request.posted_refund_id,
        }),
        enabled:
            validRequestId !== null &&
            countryScope !== null &&
            scopeBlock === null &&
            hasPostedRefund(request),
        retry: false,
    });

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId;
            const normalizedScope = nextScope == null ? null : String(nextScope);
            activeCountryScopeRef.current = normalizedScope;
            setCountryScope(normalizedScope);
            setCommand(null);
            setCommandError(null);
            setMutationResult(null);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const decisionMutation = useMutation({
        mutationFn: ({action, values, requestScope, mutationRequestId}) => {
            const perform = ACTION_REQUESTS[action];
            if (action === "approve") {
                return perform({
                    countryScope: requestScope,
                    actorCountryId: capabilities.country?.id,
                    requestId: mutationRequestId,
                });
            }
            return perform({
                countryScope: requestScope,
                actorCountryId: capabilities.country?.id,
                requestId: mutationRequestId,
                reason: values.reason,
            });
        },
        onSuccess: (updatedRequest, variables) => {
            if (getAuthToken() !== variables.requestSession) return;
            const requestDetailKey = refundKeys.requestDetail(
                variables.requestScope,
                variables.mutationRequestId,
            );
            queryClient.setQueryData(requestDetailKey, updatedRequest);
            void queryClient.invalidateQueries({
                queryKey: refundKeys.requestLists(variables.requestScope),
            });
            if (hasPostedRefund(updatedRequest)) {
                void queryClient.invalidateQueries({
                    queryKey: refundKeys.historyRoot(variables.requestScope),
                });
            }
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setCommand(null);
            setCommandError(null);
            setMutationResult(decisionResult(updatedRequest, t));
        },
        onError: async (error, variables) => {
            if (getAuthToken() !== variables.requestSession) return;
            const mapped = mapFinanceError(error);
            if (mapped.kind === "conflict") {
                const requestDetailKey = refundKeys.requestDetail(
                    variables.requestScope,
                    variables.mutationRequestId,
                );
                if (activeCountryScopeRef.current === variables.requestScope) {
                    setCommand(null);
                    setCommandError(null);
                    setMutationResult(mapped);
                }
                await Promise.all([
                    queryClient.invalidateQueries({queryKey: requestDetailKey}),
                    queryClient.invalidateQueries({
                        queryKey: refundKeys.requestLists(variables.requestScope),
                    }),
                    queryClient.invalidateQueries({
                        queryKey: refundKeys.historyRoot(variables.requestScope),
                    }),
                ]);
                return;
            }
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setCommandError(
                mapped.code === "self_approval_forbidden"
                    ? {...mapped, message: t("money.refunds.detail.selfApproval")}
                    : mapped,
            );
        },
    });

    if (validRequestId === null) {
        return <DataState title={t("money.refunds.detail.invalidTitle")} copy={t("money.refunds.detail.invalidCopy")} />;
    }

    if (scopeBlock) {
        return (
            <div className="admin-page refund-detail-page">
                <Link className="refund-back-link" to="/finance/refunds">
                    <ArrowLeft aria-hidden="true" size={16} /> {t("money.refunds.detail.back")}
                </Link>
                <p className="refund-scope-warning">{scopeBlock.title}</p>
                <DataState
                    title={scopeBlock.title}
                    copy={scopeBlock.copy}
                />
            </div>
        );
    }

    if (detailQuery.isLoading) {
        return (
            <div className="admin-table-skeleton" aria-label={t("money.refunds.detail.loading")}>
                <span /><span /><span />
            </div>
        );
    }

    if (detailQuery.isError || !request) {
        return (
            <DataState
                title={t("money.refunds.detail.loadError")}
                copy={mapFinanceError(detailQuery.error).message}
            />
        );
    }

    const status = getRefundStatusPresentation(request.status);
    const actions = actionsForRefund(request, capabilities.hasAction);
    const action = command ? getRefundActionPresentation(command) : null;

    return (
        <div className="admin-page refund-detail-page">
            <Link className="refund-back-link" to="/finance/refunds">
                <ArrowLeft aria-hidden="true" size={16} /> {t("money.refunds.detail.back")}
            </Link>
            <header className="admin-page-header refund-detail-header">
                <div>
                    <p className="admin-eyebrow">{t("money.refunds.detail.eyebrow")}</p>
                    <h1 className="admin-page-title">{t("money.refunds.detail.title")}</h1>
                    <p className="admin-page-copy">{t("money.refunds.detail.subtitle", {id: request.id, bill: request.pay_bill_id})}</p>
                </div>
                <StatusBadge tone={status.tone}>{t(`money.refunds.statuses.${status.known ? request.status : "unknown"}`)}</StatusBadge>
            </header>

            <MutationResultBanner result={mutationResult} />

            <div className="refund-detail-layout">
                <main className="refund-detail-main">
                    <section className="refund-section" aria-labelledby="refund-context-title">
                        <h2 id="refund-context-title">{t("money.refunds.detail.contextTitle")}</h2>
                        <DetailGrid items={[
                            {
                                label: t("money.refunds.detail.typeAmount"),
                                value: request.amount === null
                                    ? t("money.refunds.detail.full")
                                    : t("money.refunds.detail.partial", {amount: formatExactMoney(request.amount)}),
                            },
                            {label: t("money.refunds.detail.reason"), value: request.reason || "—"},
                            {label: t("money.refunds.detail.requester"), value: `#${request.requester_id}`},
                            {label: t("money.refunds.detail.approver"), value: request.approver_id ? `#${request.approver_id}` : "—"},
                            {label: t("money.refunds.detail.requested"), value: formatDateTime(request.requested_at)},
                            {label: t("money.refunds.detail.decision"), value: formatDateTime(request.decided_at)},
                            {label: t("money.refunds.detail.posted"), value: formatDateTime(request.posted_at)},
                            {label: t("money.refunds.detail.failureCode"), value: request.failure_code || "—"},
                            {label: t("money.refunds.detail.postingId"), value: request.posted_refund_id || "—"},
                            {label: t("money.refunds.detail.idempotencyKey"), value: request.idempotency_key},
                        ]} />
                    </section>

                    <PostedRefundEvidence request={request} query={postedHistoryQuery} />
                </main>

                <aside className="refund-action-rail" aria-label={t("money.refunds.detail.commandsAria")}>
                    <p className="admin-eyebrow">{t("money.refunds.detail.decisionEyebrow")}</p>
                    <h2>{t("money.refunds.detail.actionsTitle")}</h2>
                    {actions.length ? (
                        <div className="refund-action-list">
                            {actions.map((actionName) => {
                                const presentation = getRefundActionPresentation(actionName);
                                const Icon = ACTION_ICONS[actionName];
                                return (
                                    <button
                                        key={actionName}
                                        className="admin-button admin-button-secondary"
                                        type="button"
                                        disabled={decisionMutation.isPending}
                                        onClick={() => {
                                            setCommand(actionName);
                                            setCommandError(null);
                                            setMutationResult(null);
                                        }}
                                    >
                                        <Icon aria-hidden="true" size={16} />
                                        {t(`money.refunds.actions.${actionName}`, {defaultValue: presentation.label})}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="admin-muted">{t("money.refunds.detail.noActions")}</p>
                    )}
                </aside>
            </div>

            <FinancialCommandDialog
                open={Boolean(command)}
                dialogKey={command}
                title={action ? t("money.refunds.detail.commandTitle", {action: t(`money.refunds.actions.${command}`)}) : t("money.refunds.detail.commandFallback")}
                description={t("money.refunds.detail.commandDescription")}
                entity={t("money.refunds.detail.entity", {id: request.id})}
                details={[
                    {label: t("money.refunds.detail.currentStatus"), value: t(`money.refunds.statuses.${status.known ? request.status : "unknown"}`)},
                    {label: t("money.refunds.detail.payment"), value: request.pay_bill_id},
                    {
                        label: t("money.refunds.detail.amount"),
                        value: request.amount === null ? t("money.refunds.detail.full") : formatExactMoney(request.amount),
                    },
                ]}
                fields={actionFields(command, t)}
                pending={decisionMutation.isPending}
                error={commandError}
                confirmLabel={action ? t(`money.refunds.actions.${command}`) : t("common.confirmation.confirm")}
                onCancel={() => {
                    setCommand(null);
                    setCommandError(null);
                }}
                onConfirm={(values) => decisionMutation.mutate({
                    action: command,
                    values,
                    requestScope: countryScope,
                    requestSession: getAuthToken(),
                    mutationRequestId: validRequestId,
                })}
            />

            {detailQuery.isFetching ? (
                <span className="refund-refresh-indicator" role="status">
                    <RefreshCw aria-hidden="true" size={14} /> {t("money.refunds.detail.refreshing")}
                </span>
            ) : null}
        </div>
    );
};
